import { z } from 'zod';

import { AllocationResponseSchema } from './allocations.js';
import { NotificationResponseSchema } from './notifications.js';
import { UtilisationGranularitySchema, UtilisationModeSchema } from './utilisation.js';
import { WorkItemTreeItemSchema } from './work-items.js';

/**
 * Perspective read-model contracts (M9 — PM-2057). One call per view: the
 * work perspective (a project tree with the six tracking numbers) and the
 * resource perspective (per-resource per-period workload/capacity/utilisation,
 * or the competency view for a role). `include` is progressive disclosure
 * (Brief §13): the default response carries tracking only.
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date (YYYY-MM-DD)');

export const TrackingNumbersSchema = z.object({
  allocatedBudget: z.number(),
  registeredHours: z.number(),
  remainingWork: z.number(),
  plannedHours: z.number(),
  unplanned: z.number(),
  deviation: z.number(),
});
export type TrackingNumbersResponse = z.infer<typeof TrackingNumbersSchema>;

export const WORK_PERSPECTIVE_INCLUDES = ['tracking', 'bookings', 'conflicts'] as const;

export const WorkPerspectiveParamsSchema = z.object({ rootProjectId: z.string().uuid() });
export type WorkPerspectiveParams = z.infer<typeof WorkPerspectiveParamsSchema>;

export const WorkPerspectiveQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  granularity: UtilisationGranularitySchema.default('week'),
  /** Comma-separated subset of tracking|bookings|conflicts. Default: tracking. */
  include: z
    .string()
    .min(1)
    .default('tracking')
    .transform((value) => value.split(','))
    .pipe(z.array(z.enum(WORK_PERSPECTIVE_INCLUDES)).min(1)),
});
export type WorkPerspectiveQuery = z.infer<typeof WorkPerspectiveQuerySchema>;

/** Per-dimension tracking roll-up on a work item (`resource:<id>` / `role:<id>` / `ALL`). */
export const TrackingByKeySchema = z.object({
  key: z.string(),
  numbers: TrackingNumbersSchema,
});

export const WorkPerspectiveItemSchema = WorkItemTreeItemSchema.extend({
  /** Present when include has `tracking`: the six numbers rolled up per dimension + ALL. */
  tracking: z.array(TrackingByKeySchema).optional(),
});

export const WorkPerspectiveAllocationSchema = AllocationResponseSchema.extend({
  tracking: TrackingNumbersSchema.optional(),
});

export const PeriodBookingAggregateSchema = z.object({
  allocationId: z.string().uuid(),
  period: z.string(),
  hours: z.number(),
});

export const WorkPerspectiveResponseSchema = z.object({
  granularity: UtilisationGranularitySchema,
  workItems: z.array(WorkPerspectiveItemSchema),
  allocations: z.array(WorkPerspectiveAllocationSchema),
  /** include=bookings: per-allocation per-period booking sums at the requested granularity. */
  bookings: z.array(PeriodBookingAggregateSchema).optional(),
  /** include=conflicts: unresolved ACTIONABLE notifications anchored to any row. */
  conflicts: z.array(NotificationResponseSchema).optional(),
});
export type WorkPerspectiveResponse = z.infer<typeof WorkPerspectiveResponseSchema>;

// ---------------------------------------------------------------------------
// Resource perspective (PM-2056/PM-2057)
// ---------------------------------------------------------------------------

export const ResourcePerspectiveQuerySchema = z
  .object({
    /** Comma-separated resource uuids. Exactly one of resourceIds|departmentId|roleId. */
    resourceIds: z
      .string()
      .min(1)
      .transform((value) => value.split(','))
      .pipe(z.array(z.string().uuid()).min(1))
      .optional(),
    /** Expands to the department subtree's resources. */
    departmentId: z.string().uuid().optional(),
    /** The competency view: named + generic demand for the role. */
    roleId: z.string().uuid().optional(),
    from: isoDate,
    to: isoDate,
    granularity: UtilisationGranularitySchema.default('week'),
    mode: UtilisationModeSchema.default('raw'),
    drilldown: z.enum(['workItems']).optional(),
  })
  .refine(
    (query) =>
      [query.resourceIds, query.departmentId, query.roleId].filter((value) => value !== undefined)
        .length === 1,
    {
      message: 'Provide exactly one of resourceIds, departmentId or roleId',
      path: ['resourceIds'],
    },
  );
export type ResourcePerspectiveQuery = z.infer<typeof ResourcePerspectiveQuerySchema>;

export const WorkItemLoadSchema = z.object({
  workItemId: z.string().uuid(),
  name: z.string(),
  hours: z.number(),
});

export const ResourcePeriodSchema = z.object({
  period: z.string(),
  workloadHours: z.number(),
  capacityHours: z.number(),
  utilisationPct: z.number().nullable(),
  /** drilldown=workItems: the work items driving this period's load. */
  workItems: z.array(WorkItemLoadSchema).optional(),
});

export const ResourceUtilisationResponseSchema = z.object({
  resourceId: z.string().uuid(),
  periods: z.array(ResourcePeriodSchema),
});

export const CompetencyPeriodSchema = z.object({
  period: z.string(),
  namedHours: z.number(),
  genericHours: z.number(),
  totalHours: z.number(),
});

export const RoleDemandResponseSchema = z.object({
  roleId: z.string().uuid(),
  periods: z.array(CompetencyPeriodSchema),
});

export const ResourcePerspectiveResponseSchema = z.object({
  mode: UtilisationModeSchema,
  granularity: UtilisationGranularitySchema,
  /** Present for resourceIds/departmentId queries. */
  resources: z.array(ResourceUtilisationResponseSchema).optional(),
  /** Present for roleId queries (the competency view). */
  roles: z.array(RoleDemandResponseSchema).optional(),
});
export type ResourcePerspectiveResponse = z.infer<typeof ResourcePerspectiveResponseSchema>;
